import { useCallback, useEffect, useRef, useState } from 'react';
import { Progress } from '../Progress';
import type { ToastVariant } from './toast.types';

interface ToastProgressIndicatorProps {
  duration: number;
  variant: ToastVariant;
}

export function ToastProgressIndicator({ duration, variant }: ToastProgressIndicatorProps) {
  const [progress, setProgress] = useState(100);
  const animationFrameIdRef = useRef<number | null>(null);
  const startTimeRef = useRef<number | null>(null);

  const animate = useCallback(
    (currentTime: number) => {
      if (!startTimeRef.current) {
        startTimeRef.current = currentTime;
      }

      const elapsed = currentTime - startTimeRef.current;
      const newProgress = Math.max(0, 100 * (1 - elapsed / duration));

      setProgress(newProgress);

      if (newProgress > 0) {
        animationFrameIdRef.current = requestAnimationFrame(animate);
      }
    },
    [duration]
  );

  useEffect(() => {
    if (!duration) return;

    startTimeRef.current = null;
    animationFrameIdRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationFrameIdRef.current) {
        cancelAnimationFrame(animationFrameIdRef.current);
      }
    };
  }, [duration, animate]);

  return (
    <Progress
      className="absolute bottom-0 left-0 h-1.5 rounded-none rounded-b-full"
      variant={variant}
      value={progress}
    />
  );
}
